import React, { useMemo } from 'react';
import { Compass } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getHeatmapGradient, getHeatmapMin, getHeatmapMax, getHeatmapUnit } from '../../utils/heatmapUtils';
import './HeatmapLegend.css';

/**
 * Convert color name to hex if needed
 */
const colorToHex = (color) => {
  // If it's already a hex color, return it
  if (color.startsWith('#')) {
    return color;
  }
  
  // Basic color name to hex mapping
  const colorMap = {
    'blue': '#0000ff',
    'cyan': '#00ffff',
    'lime': '#00ff00',
    'yellow': '#ffff00',
    'orange': '#ffa500',
    'red': '#ff0000',
    'green': '#008000',
    'white': '#ffffff',
    'black': '#000000'
  };
  
  return colorMap[color.toLowerCase()] || color;
};

/**
 * Create a linear gradient from the gradient object
 */
const createGradient = (gradientObj) => {
  const stops = Object.keys(gradientObj)
    .sort((a, b) => parseFloat(a) - parseFloat(b))
    .map(key => `${colorToHex(gradientObj[key])} ${parseFloat(key) * 100}%`);
  
  return `linear-gradient(to top, ${stops.join(', ')})`;
};

/**
 * Calculate average wind direction from stations
 */
const calculateAverageWindDirection = (stations) => {
  if (!stations || stations.length === 0) return null;
  
  let sumSin = 0;
  let sumCos = 0;
  let count = 0;
  
  stations.forEach(station => {
    if (!station.currentReadings || !station.isActive) return;
    
    const windDirection = station.currentReadings.windDirection;
    if (windDirection !== undefined && windDirection !== null) {
      const radians = (windDirection * Math.PI) / 180;
      sumSin += Math.sin(radians);
      sumCos += Math.cos(radians);
      count++;
    }
  });
  
  if (count === 0) return null;
  
  // Calculate average direction using vector addition
  const avgSin = sumSin / count;
  const avgCos = sumCos / count;
  let avgDirection = Math.atan2(avgSin, avgCos) * 180 / Math.PI;
  
  // Normalize to 0-360
  if (avgDirection < 0) avgDirection += 360;
  
  return Math.round(avgDirection);
};

/**
 * Convert degrees to direction name
 */
const getDirectionName = (degrees) => {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 
                      'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
};

export function HeatmapLegend({ dataType, minValue, maxValue, visible = false, stations = [] }) {
  const { t } = useTranslation();
  const gradient = useMemo(() => getHeatmapGradient(dataType), [dataType]);
  const min = minValue !== undefined ? minValue : getHeatmapMin(dataType);
  const max = maxValue !== undefined ? maxValue : getHeatmapMax(dataType);
  const unit = getHeatmapUnit(dataType);
  const label = useMemo(() => {
    switch (dataType) {
      case 'temperature':
        return t('map.heatmapTypes.temperature');
      case 'windSpeed':
        return t('map.heatmapTypes.windSpeed');
      case 'windDirection':
        return t('map.heatmapTypes.windDirection');
      case 'humidity':
        return t('map.heatmapTypes.humidity');
      case 'solarRadiation':
        return t('map.heatmapTypes.solarRadiation');
      default:
        return t('map.heatmapTypes.heatmap');
    }
  }, [dataType, t]);
  
  // Calculate average wind direction for all heatmap types
  const averageWindDirection = useMemo(() => {
    if (stations && stations.length > 0) {
      return calculateAverageWindDirection(stations);
    }
    return null;
  }, [stations]);
  
  if (!visible) {
    return null;
  }
  
  const gradientStyle = {
    background: createGradient(gradient)
  };
  
  // Generate tick marks
  const ticks = [];
  const numTicks = 6;
  for (let i = 0; i <= numTicks; i++) {
    const value = min + (max - min) * (i / numTicks);
    ticks.push({
      value: value.toFixed(1),
      position: (i / numTicks) * 100
    });
  }
  
  return (
    <div className="heatmap-legend">
      <div className="heatmap-legend__header">
        <span className="heatmap-legend__title">{label}</span>
        <span className="heatmap-legend__unit">{unit}</span>
      </div>
      <div className="heatmap-legend__scale">
        <div className="heatmap-legend__gradient" style={gradientStyle} />
        <div className="heatmap-legend__ticks">
          {ticks.map((tick, index) => (
            <div
              key={index}
              className="heatmap-legend__tick"
              style={{ bottom: `${tick.position}%` }}
            >
              <span className="heatmap-legend__tick-label">{tick.value}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Wind Direction Indicator - Show for all heatmap types */}
      {averageWindDirection !== null && (
        <div className="heatmap-legend__wind-direction">
          <div className="heatmap-legend__wind-direction-header">
            <Compass size={14} />
            <span>{t('map.windDirection')}</span>
          </div>
          <div className="heatmap-legend__compass">
            <div 
              className="heatmap-legend__compass-rose"
              style={{ transform: `rotate(${averageWindDirection}deg)` }}
            >
              <div className="heatmap-legend__compass-arrow" />
            </div>
            <div className="heatmap-legend__compass-labels">
              <span className="heatmap-legend__compass-label heatmap-legend__compass-label--n">{t('map.heatmapLegend.directions.n')}</span>
              <span className="heatmap-legend__compass-label heatmap-legend__compass-label--e">{t('map.heatmapLegend.directions.e')}</span>
              <span className="heatmap-legend__compass-label heatmap-legend__compass-label--s">{t('map.heatmapLegend.directions.s')}</span>
              <span className="heatmap-legend__compass-label heatmap-legend__compass-label--w">{t('map.heatmapLegend.directions.w')}</span>
            </div>
          </div>
          <div className="heatmap-legend__wind-direction-value">
            {averageWindDirection}° {getDirectionName(averageWindDirection)}
          </div>
        </div>
      )}
    </div>
  );
}

